import { useContext, useEffect, useRef, useState } from "react"
import LessonOverlapWarning from "../week/LessonOverlapWarning"
import { Div, Col, Row, Switch, Label, Text, Button, Icon, Checkbox } from "atomize"
import PrimaryButton from "../buttons/PrimaryButton"
import SecondaryButton from "../buttons/SecondaryButton"
import { theme } from "../settings/config"
import Container from "../atoms/Container"
import DateInput from "../inputs/DateInput"
import { DateTime } from "luxon"
import Paragraph from "../atoms/Paragraph"
import TimeInput from "../inputs/TimeInput"
import { playersContext } from "../providers/players.provider"
import TextInput from "../inputs/TextInput"
import { motion } from "framer-motion"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
	faEuro,
	faFireAlt,
	faLightbulb,
	faMinus,
	faLock,
	faPlus,
	faTrash,
	faXmark,
} from "@fortawesome/free-solid-svg-icons"
import InlineCheckboxButtons from "../InlineCheckboxButtons"
import TextArea from "../inputs/TextArea"
import CoachAvatar from "../atoms/CoachAvatar"
import { profileContext } from "../providers/profile.provider"
import { lessonsContext } from "../providers/lessons.provider"
import {
	getLessonFillColor,
	getPaymentAmount,
	getDefaultLessonDuration,
	defaultLessonTimes,
	lessonTimesWithinDay,
	getPaymentAmountByType,
	getSportIcon,
	lessonTypeHasCourt,
	lessonTypeHasPlayers,
	isPlaceholderPartecipant,
	getPlaceholderLabel,
	markCustomPaymentAmounts,
	paymentAmountAfterEdit,
	paymentCoveredElsewhere,
	playerCanPlay,
	savedPaymentAmount,
	getPlayerFullName,
	getPlayerLabel,
	getPaymentMethodName,
	findKnownPlayer,
	normalizeName,
	getAssignableCoaches,
	getSchoolCoaches,
	getViewableCoaches,
	teachesInSchool,
	canAssignLessonTo,
	lessonTypeAllowsAssistants,
	getSatLevel,
	lessonTypeHasSatLevel,
	SAT_LEVELS,
	satLevelToSave,
	getPtrLevel,
	lessonTypeDisplayNameOf,
	lessonTypeHasPtrLevel,
	ptrLevelToSave,
	PRIVATE_LESSON_LABEL,
	isPrivateLessonType,
	privateLessonSize,
	privateLessonTypeFor,
	sortPrivateLessonTypes,
} from "../utils/func.utils"
import {
	isSubscriptionCourse,
	keptSubstitutes,
	lessonSubstitutes,
	priceLessonTypeFor,
	regularPartecipants,
	substituteKey,
	substituteOf,
	substitutePaidToFor,
	withSubstitute,
} from "../utils/substitutes.utils"
import useRecoveriesLeft from "../lesson/useRecoveriesLeft"
import PaidToChoice from "../lesson/PaidToChoice"
import PresenceSwitch from "../atoms/Switch"
import Dropdown from "../atomize/Dropdown"
import { faLightbulb as faLightbulbOutline } from "@fortawesome/free-regular-svg-icons"
import {
	addLesson,
	editLesson,
	linkRescheduledLesson,
	resolveLessonPartecipants,
} from "../utils/firestore.utils"
import { Timestamp } from "firebase/firestore"
import { v4 as uuidv4 } from "uuid"
import { schoolContext } from "../providers/school.provider"
import { getSportLessonTypes } from "../utils/school.utils"
import {
	BILLED_TO_PLAYERS,
	BILLED_TO_SCHOOL,
	COURT_PAID_BY_COACH,
	COURT_PAID_BY_PLAYERS,
	formatAmount,
	getCourtExtras,
	getCourtFeeAtDate,
	getCourtShare,
	getLessonBilledTo,
	getPlayerPrice,
	getSchoolRates,
	isCourtPaidByPlayers,
	isPaidToSchool,
	isSchoolBilledType,
	PAID_TO_SCHOOL,
	SUBSTITUTE_PAID_TO_SCHOOL,
} from "../utils/billing.utils"
import { sharedDataContext } from "../providers/sharedData.provider"
import Divider from "../atoms/Divider"
import { isSmartphone } from "../atoms/SmartphoneView"
import RadioBox from "../atoms/RadioBox"
import CourtBlock from "../lesson/CourtBlock"
import { toast } from "react-toastify"
import { useSyncStatus } from "../services/sync.service"
import { networkAwareMessage } from "../utils/network.utils"
import PtrLevelPicker from "../lesson/PtrLevelPicker"
import LessonTypeLabel from "../atoms/LessonTypeLabel"
import DashedOutline from "../atoms/DashedOutline"
import { isMatchLessonType } from "../utils/match.utils"

// Quanti assistenti al massimo: le regole ne controllano uno per uno fino a
// qui (vedi "GLI ASSISTENTI" in firestore.rules), e una lezione con di più
// non si salverebbe.
const MAX_ASSISTANTS = 5

// `startsAs` è la tipologia da cui parte una lezione nuova: di solito la
// privata, e "Gioco io" quando lo si sceglie dal menu del "+".
export function LessonFormModal({
	mode,
	closeModal,
	lessonData,
	selectedDate,
	startsAs = "pri1",
}) {
	// Una lezione nuova parte dal giorno che si sta guardando. Se chi apre il
	// modulo non lo dice — capita, ed è una riga di codice in meno che
	// sembra innocua — si parte da oggi: senza, `DateTime.fromISO(undefined)`
	// è una data non valida, `toISO()` restituisce null e la schermata muore
	// prima di comparire.
	const startingDate = selectedDate ?? DateTime.now().toFormat("yyyy-MM-dd")
	const isNew = mode === "new"
	const isEdit = mode === "edit"
	// Riprogrammare il recupero di una lezione annullata è duplicarla: una
	// lezione nuova, con gli abbonati che pagano col recupero
	const isReschedule = mode === "reschedule"
	// Reinserire una lezione eliminata, dal registro: anche questa è una
	// lezione nuova fatta da una che c'era, come duplicandola. Se era una
	// serie, la ricorrenza parte già accesa fino all'ultima data eliminata
	// (`reinsertLessonData` in src/utils/authorship.utils.js).
	const isReinsert = mode === "reinsert"
	const isDuplicate = mode === "duplicate" || isReschedule || isReinsert

	const { activePlayers } = useContext(playersContext)
	const { coach, displayedCoachID } = useContext(profileContext)
	const { removeLessonFromCache, invalidateDate } = useContext(lessonsContext)
	const { getSelectedSchool, selectedSchool, getSchoolSettings } = useContext(schoolContext)
	// prezzi e metodi di pagamento sono le condizioni del maestro in questo
	// circolo, non proprietà del circolo: dove non ne ha di sue eredita quelle
	// comuni, e da qui la differenza non si vede
	const schoolSettings = getSchoolSettings()
	const { getPaymentMethod, getLessonType, getSport, lessonTypes, sports } =
		useContext(sharedDataContext)

	// I maestri del circolo, una volta caricati: prima sono reference, e da
	// una reference non si legge nemmeno il nome.
	const schoolCoachByID = coachID => {
		const schoolCoaches = getSelectedSchool()?.coaches
		return Array.isArray(schoolCoaches) ? undefined : schoolCoaches?.[coachID]
	}
	const coachNameOf = coachID =>
		coachID === coach?.coachID ? "Tu" : (schoolCoachByID(coachID)?.coachName ?? "")
	// per la foto: sé stessi si ha già, e così c'è anche prima dei colleghi
	const coachByID = coachID => (coachID === coach?.coachID ? coach : schoolCoachByID(coachID))

	// Per chi si può mettere la lezione: sé, e i colleghi di cui si tengono
	// le lezioni — tutti, per chi dirige il circolo.
	const assignableCoaches = getAssignableCoaches(coach, getSelectedSchool(), selectedSchool)

	// Una lezione nuova parte dal maestro che si sta guardando, nella
	// giornata o nel riepilogo: chi guarda Giulia e tocca "+" si aspetta una
	// lezione di Giulia. Se non se ne guarda nessuno in particolare — o
	// "Tutti", o un collega non ancora caricato — si parte da sé.
	const displayedCoach =
		displayedCoachID && displayedCoachID !== coach?.coachID
			? schoolCoachByID(displayedCoachID)
			: undefined
	// Quando giochi tu, giochi tu: chiunque si stia guardando.
	const startingCoach =
		displayedCoach?.taughtSports?.length && !isMatchLessonType(startsAs) ? displayedCoach : coach

	const [formStep, setFormStep] = useState(0)
	// Modificando, il maestro resta quello della lezione anche se i colleghi
	// non sono ancora arrivati: senza, salvare la riscriverebbe senza maestro.
	const [lessonCoach, setLessonCoach] = useState(
		isNew
			? startingCoach
			: lessonData.coachID === coach?.coachID
				? coach
				: (schoolCoachByID(lessonData.coachID) ?? { coachID: lessonData.coachID }),
	)
	const [assistantCoaches, setAssistantCoaches] = useState(
		isNew ? [] : lessonData?.assistantCoachIDs || [],
	)
	const [showAssistants, setShowAssistants] = useState(false)
	const [dropdownIsOpen, setDropdownOpenState] = useState(false)
	const [lessonDate, setLessonDate] = useState(
		isNew
			? startingDate
			: DateTime.fromJSDate(lessonData.lessonStart.toDate()).toFormat("yyyy-MM-dd"),
	)
	// una lezione nuova parte dalla sua tipologia: la sua durata, dall'ora
	// di adesso
	const proposedTimes = defaultLessonTimes(
		DateTime.now(),
		getDefaultLessonDuration(getLessonType(startsAs)),
	)
	const [lessonStartTime, setLessonStartTime] = useState(
		isNew
			? proposedTimes.start
			: DateTime.fromJSDate(lessonData.lessonStart.toDate()).toFormat("HH:mm"),
	)
	const [lessonEndTime, setLessonEndTime] = useState(
		isNew
			? proposedTimes.end
			: DateTime.fromJSDate(lessonData.lessonEnd.toDate()).toFormat("HH:mm"),
	)
	// `pri1` e non `"pri"`, che tipo di lezione non è: l'effetto sul numero di
	// allievi lo correggeva subito, ma nel frattempo `getLessonType` su quel
	// valore tornava niente, e chi ci si appoggia si aspetta un documento.
	const [lessonType, setLessonType] = useState(isNew ? startsAs : lessonData?.lessonType)
	const [sportID, setSportID] = useState(
		isNew ? lessonCoach.taughtSports[0] : lessonData?.sportID,
	)
	// I sostituti prendono il posto di un assente: non sono allievi in più,
	// e il modulo non li conta. Restano da parte e tornano nella lezione
	// quando la si salva — finché c'è ancora chi sostituiscono.
	const savedRegulars = isNew ? [] : regularPartecipants(lessonData?.partecipants)
	const savedSubstitutes = isEdit ? lessonSubstitutes(lessonData?.partecipants) : []
	const [totalPlayers, setTotalPlayers] = useState(
		isNew ? (lessonTypeHasPlayers(getLessonType(startsAs)) ? 1 : 0) : savedRegulars.length,
	)
	const [court, setCourt] = useState(
		isNew
			? {
					courtNumber: Object.keys(
						getSelectedSchool().courts?.[lessonCoach.taughtSports[0]] ?? {},
					)[0],
					courtsSport: lessonCoach.taughtSports[0],
				}
			: lessonData?.court,
	)
	const [usingLights, setUsingLights] = useState(isNew ? false : lessonData?.usingLights)
	const [usingHeat, setUsingHeat] = useState(isNew ? false : lessonData?.usingHeat)
	// Chi paga il campo: di norma quello che vale nel circolo, ma questa
	// lezione può fare di testa sua. Una lezione già salvata tiene la scelta
	// che ha — e se non ne ha è di prima che si potesse scegliere, quando il
	// campo era a carico del maestro: modificarla non deve riscriverne la
	// storia. Duplicare invece fa una lezione nuova, e dove non c'è niente da
	// copiare vale la regola di oggi.
	const [courtPaidBy, setCourtPaidBy] = useState(
		isNew
			? schoolSettings.courtPaidBy
			: isEdit
				? (lessonData?.courtPaidBy ?? COURT_PAID_BY_COACH)
				: (lessonData?.courtPaidBy ?? schoolSettings.courtPaidBy),
	)
	// Le tariffe del circolo dicono anche quali lezioni si fatturano a lui:
	// quelle non si scontano del campo, perché il campo lì non c'entra.
	const schoolRates = getSchoolRates(coach?.billingData, selectedSchool)

	// La quota di campo di un allievo in una lezione fatta così: è quello che
	// non gli si chiede, perché il campo lo paga lui al circolo. Le lezioni
	// fatturate al circolo e quelle senza campo non ne hanno nessuna.
	const courtShareOf = ({
		billedToSchool,
		sportID,
		usingLights,
		usingHeat,
		court,
		courtPaidBy,
		date,
		hours,
		players,
	}) => {
		if (!isCourtPaidByPlayers(courtPaidBy)) return 0
		if (billedToSchool) return 0
		if (Number(court?.courtNumber ?? court ?? 0) === 0) return 0

		return getCourtShare(schoolSettings.courtsFee, {
			sportID,
			usingLights,
			usingHeat,
			date,
			hours,
			playersCount: players,
		})
	}

	const savedLessonHours = isNew
		? 0
		: DateTime.fromJSDate(lessonData.lessonEnd.toDate())
				.diff(DateTime.fromJSDate(lessonData.lessonStart.toDate()), "hours")
				.toObject().hours

	// Il prezzo che il listino dava alla lezione com'è salvata: è il metro per
	// capire se l'importo di un allievo è quello standard o una correzione.
	const savedListPrice = isNew
		? null
		: getPlayerPrice(
				getPaymentAmount(
					schoolSettings.lessonPrices,
					priceLessonTypeFor(lessonData.lessonType, savedRegulars.length, lessonTypes),
					lessonData.sportID,
					savedLessonHours,
				),
				courtShareOf({
					billedToSchool:
						getLessonBilledTo(lessonData, schoolRates) === BILLED_TO_SCHOOL,
					sportID: lessonData.sportID,
					usingLights: lessonData.usingLights,
					usingHeat: lessonData.usingHeat,
					court: lessonData.court,
					courtPaidBy: lessonData.courtPaidBy,
					date: lessonData.lessonStart,
					hours: savedLessonHours,
					players: savedRegulars.length,
				}),
			)

	// Gli allievi come sono salvati, già marcati: punto di partenza del
	// modulo, e a cui tornare se un allievo tolto viene rimesso. Duplicando
	// si fa una lezione nuova, che non si è ancora fatta: tutti presenti,
	// come in ogni lezione che nasce, qualunque cosa dicesse l'originale.
	// Reinserendo invece si rimette una lezione che c'era, con chi mancava.
	const savedPartecipants = isNew
		? []
		: markCustomPaymentAmounts(savedRegulars, savedListPrice).map(partecipant =>
				isDuplicate && !isReinsert ? { ...partecipant, isPresent: true } : partecipant,
			)

	const [partecipants, setPartecipants] = useState(savedPartecipants)
	const [substitutes, setSubstitutes] = useState(savedSubstitutes)
	const [lessonNotes, setLessonNotes] = useState(isNew ? "" : lessonData?.lessonNotes)
	// Il livello del SAT, se lo si vuole dire. Duplicando si rifà lo stesso
	// gruppo, quindi lo stesso livello.
	const [satLevel, setSatLevel] = useState(isNew ? null : (lessonData?.satLevel ?? null))
	// E quello PTR del CAD, allo stesso modo.
	const [ptrLevel, setPtrLevel] = useState(isNew ? null : (lessonData?.ptrLevel ?? null))
	const reinsertUntil = isReinsert ? (lessonData?.reinsertUntil ?? null) : null
	const [isRecurrent, setLessonRecurrent] = useState(Boolean(reinsertUntil))
	const [recurrentLessonDateEnd, setRecurrentLessonDateEnd] = useState(
		reinsertUntil ??
			DateTime.fromISO(
				isNew
					? startingDate
					: DateTime.fromJSDate(lessonData.lessonStart.toDate()).toFormat("yyyy-MM-dd"),
			)
				.plus({ week: 1 })
				.toISO()
				.split("T")[0],
	)
	const [editSeries, setEditSeries] = useState(false)
	// Le serie già salvate, senza rete, non si modificano: lo si dice qui,
	// prima di premere "Salva", e non solo dopo.
	const { online } = useSyncStatus()
	const [isSubmitting, setIsSubmitting] = useState(false)
	const lastFormStep = 2

	const currentLessonHours = DateTime.fromISO(`${lessonDate}T${lessonEndTime}`)
		.diff(DateTime.fromISO(`${lessonDate}T${lessonStartTime}`), "hours")
		.toObject().hours

	// A chi si fattura questa lezione. Una lezione già salvata tiene quello
	// che si era scritta, finché resta del tipo che era: cambiare tipo qui
	// dentro vuol dire cambiare lezione, e allora vale il gruppo di oggi.
	// Anche duplicare fa una lezione nuova, che nasce con la regola di adesso.
	const billedTo =
		isEdit && lessonType === lessonData.lessonType
			? getLessonBilledTo(lessonData, schoolRates)
			: isSchoolBilledType(schoolRates, lessonType)
				? BILLED_TO_SCHOOL
				: BILLED_TO_PLAYERS

	// Chi paga il campo conta solo nelle lezioni fatturate ai privati: quelle
	// che fatturi al circolo non hanno un campo a carico né tuo né degli
	// allievi, e la scelta non va né mostrata né scritta sulla lezione.
	const isBilledToSchool = billedTo === BILLED_TO_SCHOOL

	// Se di questa lezione si segnano i nomi, e se occupa un campo: due
	// domande diverse, e le risponde la tipologia.
	const hasPlayers = lessonTypeHasPlayers(getLessonType(lessonType))
	const hasCourt = lessonTypeHasCourt(getLessonType(lessonType))

	// "Gioco io": quando giochi tu (src/utils/match.utils.js). Ha il campo e
	// basta, il campo lo paghi tu, e il modulo non parla di lezione.
	const isMatchForm = isMatchLessonType(lessonType)
	// Si gioca sul campo di uno sport che non è per forza quello che
	// insegni: è quello che dice quale tariffa pagare.
	const lessonSportID = isMatchForm ? (court?.courtsSport ?? sportID) : sportID

	// Il colore del tipo di lezione è il tema del modulo: la tipologia
	// scelta, la linea sotto, i più e meno, i pagamenti, gli interruttori e
	// il bottone per andare avanti. Ci sta sopra la scritta bianca, quindi è
	// il pieno del tipo e non la tinta cupa: nello scuro quella diventa
	// chiara, e "Avanti" in bianco su un celestino non si leggeva.
	//
	// Tranne quando giochi tu: il grigio di "Gioco io" sotto "Conferma" sembra
	// un bottone spento. Lì l'accento è l'arancione dell'app.
	const typeAccent = isMatchForm
		? theme.colors.primary
		: getLessonFillColor(getLessonType(lessonType))

	// Gli assistenti si propongono solo dove la tipologia vuole più istruttori
	// in campo: un SAT sì, una privata mai. Una lezione già salvata con gli
	// assistenti su una tipologia che adesso non li vuole se li tiene, finché
	// non le si cambia la tipologia: non si toglie niente a nessuno.
	const assistantsAllowed = lessonTypeAllowsAssistants(getLessonType(lessonType))
	const keepsSavedAssistants = !isNew && lessonType === lessonData?.lessonType
	const lessonAssistants = assistantsAllowed || keepsSavedAssistants ? assistantCoaches : []

	// Le tipologie che qui si fanno: quelle che il maestro ha in lista, o
	// quelle che lo sport propone se non ha mai scelto. Si scelgono tutte
	// dalla riga in cima.
	const lessonTypesHere = getSportLessonTypes(
		schoolSettings,
		sportID,
		sports[sportID],
		getLessonType,
	)

	// Quelle con i nomi da segnare, e tra queste le private: nella riga in
	// cima sono una pastiglia sola, "Privata", e quale delle quattro lo dice
	// il numero di giocatori.
	const playerLessonTypesHere = lessonTypesHere.filter(lessonTypeID =>
		lessonTypeHasPlayers(lessonTypes[lessonTypeID]),
	)
	const isPrivate = isPrivateLessonType(lessonType)
	// Una lezione salvata con un tipo che qui non si propone più se lo
	// tiene: modificarla non deve cambiarle tipo di nascosto.
	const keepsSavedType = !isNew && hasPlayers && !playerLessonTypesHere.includes(lessonType)
	const privateLessonTypes = sortPrivateLessonTypes(
		keepsSavedType && isPrivate ? [...playerLessonTypesHere, lessonType] : playerLessonTypesHere,
	)
	const courseLessonTypes = [
		...playerLessonTypesHere.filter(lessonTypeID => !isPrivateLessonType(lessonTypeID)),
		...(keepsSavedType && !isPrivate ? [lessonType] : []),
	]

	// Quanti giocatori può avere una lezione di questo tipo. Le private
	// vanno dalla più piccola alla più grande che si fanno qui; le altre
	// stanno tra il minimo e il massimo del catalogo, e senza un massimo
	// non c'è un tetto.
	const playersRangeOf = lessonTypeID => {
		if (isPrivateLessonType(lessonTypeID)) {
			const sizes = privateLessonTypes.map(privateLessonSize)
			return { min: sizes[0] ?? 1, max: sizes[sizes.length - 1] ?? 1 }
		}
		const typeData = getLessonType(lessonTypeID)
		return {
			min: typeData?.minimumPlayers || 1,
			max: typeData?.maxPlayers || Infinity,
		}
	}
	const playersRange = playersRangeOf(lessonType)

	// Più e meno: nelle private si passa da una privata che c'è alla
	// successiva, così da tre si va a quattro anche se manca la privata da
	// tre; nelle altre si va di uno in uno, dentro il minimo e il massimo.
	const nextPlayersCount = step => {
		if (isPrivate) {
			const sizes = privateLessonTypes.map(privateLessonSize)
			return step > 0
				? sizes.find(size => size > totalPlayers)
				: [...sizes].reverse().find(size => size < totalPlayers)
		}
		const next = totalPlayers + step
		return next >= playersRange.min && next <= playersRange.max ? next : undefined
	}

	const lessonCourtPaidBy = isBilledToSchool || isMatchForm ? COURT_PAID_BY_COACH : courtPaidBy

	// Quello che il campo costa a ogni allievo della lezione che si sta
	// scrivendo: zero finché il campo lo paga il maestro.
	const currentCourtShare = courtShareOf({
		billedToSchool: isBilledToSchool,
		sportID,
		usingLights,
		usingHeat,
		court,
		courtPaidBy: lessonCourtPaidBy,
		date: lessonDate,
		hours: currentLessonHours,
		players: totalPlayers,
	})

	// Quanto chiede il listino per la lezione così com'è adesso nel modulo,
	// meno il campo se lo pagano loro: è il prezzo di partenza di ogni
	// allievo, e il metro per dire se un importo è una correzione del maestro
	// o no.
	//
	// Una lezione fatturata al circolo non ha un prezzo per allievo: chi
	// viene ha l'abbonamento. Il listino dei privati non la riguarda, e non
	// c'è più bisogno di metterlo a zero per farla stare zitta.
	const currentFullPrice = isBilledToSchool
		? 0
		: getPaymentAmount(
				schoolSettings.lessonPrices,
				priceLessonTypeFor(lessonType, totalPlayers, lessonTypes),
				sportID,
				currentLessonHours,
			)
	const currentListPrice = getPlayerPrice(currentFullPrice, currentCourtShare)
	const substitutePrice = getPlayerPrice(
		getPaymentAmount(
			schoolSettings.lessonPrices,
			priceLessonTypeFor(lessonType, totalPlayers, lessonTypes),
			sportID,
			currentLessonHours,
		),
		currentCourtShare,
	)

	// Quanto costa il campo all'ora in questa lezione: serve a dire in chiaro
	// da dove viene la quota tolta agli allievi, o cosa ti si scala se lo
	// paghi tu.
	const currentCourtFee = getCourtFeeAtDate(
		schoolSettings.courtsFee,
		lessonSportID,
		{ usingLights, usingHeat },
		lessonDate,
	)

	// Luce e riscaldamento si chiedono solo dove cambiano un prezzo: al Siro
	// il riscaldamento sì e la luce no, a Castel Maggiore né l'uno né l'altra,
	// e chiederlo lo stesso sarebbe una domanda senza risposta utile.
	const courtExtras = getCourtExtras(schoolSettings.courtsFee, lessonSportID)

	const isCourse = isSubscriptionCourse(lessonType)

	// ──────────────────────────────────────────────────────────────────
	// CHI PAGA IL CIRCOLO
	//
	// Sotto i metodi di pagamento di ogni allievo c'è "Ha pagato al
	// circolo": chi ha pagato in segreteria non ha pagato te, e un metodo
	// non ce l'ha. Il riepilogo fattura al circolo quello che ha pagato
	// meno la sua quota di campo. Di solito
	// vale quello che dicono le tue condizioni nel circolo, ma ognuno fa
	// per sé.
	//
	// Non c'è nelle lezioni fatturate al circolo, dove gli allievi non
	// pagano niente, e non tocca i sostituti dei corsi: chi incassa quelli
	// lo dice già la regola del corso.
	// ──────────────────────────────────────────────────────────────────
	const paidToSchoolByDefault = isPaidToSchool(schoolSettings.playersPayTo)
	const canPaySchool = !isBilledToSchool
	const canSubstitutePaySchool = canPaySchool && !isCourse

	// Chi entra nella lezione senza aver ancora scelto come paga parte da qui
	const defaultPaidTo =
		canPaySchool && paidToSchoolByDefault ? { paidTo: PAID_TO_SCHOOL } : {}

	const withoutPaidTo = ({ paidTo, ...player }) => player

	// Se ha pagato il circolo, dove lo si può dire
	const paysSchool = (player, canChoose) => canChoose && isPaidToSchool(player?.paidTo)

	// ──────────────────────────────────────────────────────────────────
	// CHI NEI CORSI PAGA LA VOLTA
	//
	// In un corso fatturato al circolo chi viene senza abbonamento — uno in
	// più nel CAD da tre — paga come il sostituto: quanto una privata con gli
	// allievi della lezione, e i soldi sono del circolo. Si segna solo chi li
	// ha presi, partendo da quello che dice il corso; presi da te, il
	// riepilogo li mette in "Da dare al circolo".
	// ──────────────────────────────────────────────────────────────────
	const coursePaidTo = substitutePaidToFor(lessonType, getLessonType(lessonType))
	const canPayVisit = isCourse && isBilledToSchool

	const paysVisit = player =>
		canPayVisit &&
		!isPlaceholderPartecipant(player) &&
		Boolean(player?.paymentMethod) &&
		!paymentCoveredElsewhere(player.paymentMethod)

	// Il prezzo di partenza di un allievo: quello della lezione, o quello di
	// chi viene la volta
	const listPriceFor = player => (paysVisit(player) ? substitutePrice : currentListPrice)

	const handleVisitPaidToChange = (i, paidTo) =>
		setPartecipants(prev =>
			prev.map((player, index) => (index === i ? { ...player, paidTo } : player)),
		)

	// L'interruttore sta sotto la riga dei metodi e non dentro: in fondo a
	// una riga che scorre finiva fuori dallo schermo, e non lo trovava
	// nessuno. Chi recupera o ha l'abbonamento non paga la volta, e non ce
	// l'ha.
	const paidToSchoolSwitch = (player, onToggle) =>
		!paymentCoveredElsewhere(player?.paymentMethod) && (
			<Label
				onClick={onToggle}
				display="flex"
				justify="space-between"
				align="center"
				m={{ b: "1rem" }}>
				<Text textSize="subheader">Ha pagato al circolo</Text>
				<Switch
					m={{ l: "0.5rem" }}
					shadow="0"
					checked={isPaidToSchool(player?.paidTo)}
					activeColor={typeAccent}
					inactiveColor="gray_light"
				/>
			</Label>
		)

	// Il prezzo si chiede solo a chi paga la volta: l'abbonamento e il
	// recupero sono già pagati, e una lezione del circolo non ha prezzi. Un
	// posto ancora senza nome non paga niente: il prezzo arriva col nome.
	const showsPrice = player =>
		(!isBilledToSchool || paysVisit(player)) &&
		!paymentCoveredElsewhere(player?.paymentMethod) &&
		!isPlaceholderPartecipant(player)

	// Recupero solo per chi ha lezioni da recuperare di questo corso in questa
	// stagione — o per chi l'ha già scelto qui. L'abbonamento c'è sempre nei corsi, dove
	// è il modo normale di pagare.
	const recoveries = useRecoveriesLeft(
		[...partecipants, ...substitutes].map(player => player.playerID),
		DateTime.fromISO(lessonDate).toJSDate(),
		isEdit ? lessonData?.lessonID : null,
		lessonType,
	)
	const paymentMethodsFor = player => {
		const methods = (schoolSettings.availablePaymentMethods || []).filter(
			method => method !== "recovery",
		)
		if (isCourse && !methods.includes("subscription")) methods.unshift("subscription")
		if (recoveries[player?.playerID] > 0 || player?.paymentMethod === "recovery")
			methods.push("recovery")
		return methods
	}

	// Presenze e sostituti si segnano su una lezione che c'è: una nuova, o
	// una serie, li copierebbe su tutte le date. E riguardano chi ha
	// l'abbonamento, l'unico che ha una lezione da recuperare se manca.
	const tracksPresence = player =>
		isEdit && player?.paymentMethod === "subscription" && !isPlaceholderPartecipant(player)

	const handlePresenceToggle = i => {
		setPartecipants(prev =>
			prev.map((player, index) =>
				index === i ? { ...player, isPresent: player.isPresent === false } : player,
			),
		)
	}

	// Un sostituto recupera o paga la volta, come dalla lezione: niente
	// abbonamento, e il recupero solo a chi ne ha.
	const substitutePaymentMethodsFor = substitute => [
		...(recoveries[substitute?.playerID] > 0 || substitute?.paymentMethod === "recovery"
			? ["recovery"]
			: []),
		...(schoolSettings.availablePaymentMethods || []).filter(
			method => method !== "recovery" && method !== "subscription",
		),
	]

	const updateSubstitute = (absent, changes) => {
		setSubstitutes(prev => {
			const current = substituteOf(prev, absent) ?? {
				playerName: "",
				playerID: "",
				paymentMethod: null,
				paymentAmount: 0,
			}
			return withSubstitute(prev, absent, { ...current, ...changes })
		})
	}

	// Chi è l'allievo che si sta scrivendo. Toccato nell'elenco, il campo
	// porta l'etichetta con il soprannome e il nome che resta è quello vero.
	// Scritto a mano, lo si riconosce subito con lo stesso confronto che fa il
	// salvataggio — nome completo, etichetta o soprannome — così il recupero
	// si propone già qui. Il testo resta com'è: riscriverlo sotto le dita
	// mentre si digita sarebbe peggio.
	const recognizeTypedPlayer = value => {
		const chosen = activePlayers.find(player => getPlayerLabel(player) === value)
		if (chosen) return { playerName: getPlayerFullName(chosen), playerID: chosen.playerID }
		return { playerName: value, playerID: findKnownPlayer(value, activePlayers)?.playerID ?? "" }
	}

	// Sotto il nome, chi si è riconosciuto quando non lo dice già il testo
	// ("Bibo" → Marco Rossi), o che non c'è ancora: un nome sbagliato si vede
	// qui, prima di salvare un doppione.
	const typedPlayerHint = player => {
		const typed = player?.playerName?.trim()
		if (!typed || activePlayers.length === 0) return null
		if (!player.playerID) return { text: "Nuovo allievo", known: false }
		const known = activePlayers.find(candidate => candidate.playerID === player.playerID)
		if (!known || normalizeName(getPlayerFullName(known)) === normalizeName(typed)) return null
		return { text: getPlayerLabel(known), known: true }
	}

	const renderTypedPlayerHint = player => {
		const hint = typedPlayerHint(player)
		if (!hint) return null
		return (
			<Text
				textSize="caption"
				textColor={hint.known ? theme.colors.green : theme.colors.text_light}
				m={{ t: "0.25rem" }}>
				{hint.known ? `\u2713 ${hint.text}` : hint.text}
			</Text>
		)
	}

	const handleSubstituteChange = (e, absent) => {
		const { playerName, playerID } = recognizeTypedPlayer(e.target.value)
		// chi ha lezioni da recuperare di solito viene per quello, e allora non
		// paga nessuno; gli altri, fuori dai corsi, partono da a chi pagano di
		// solito gli allievi
		const recovers = recoveries[playerID] > 0
		const paysSchool = canSubstitutePaySchool && Boolean(defaultPaidTo.paidTo) && !recovers

		updateSubstitute(absent, {
			playerName,
			playerID,
			paymentMethod: recovers ? "recovery" : null,
			paymentAmount: paysSchool ? substitutePrice : 0,
			paymentAmountIsCustom: false,
			...(canSubstitutePaySchool && { paidTo: paysSchool ? PAID_TO_SCHOOL : undefined }),
			// nei corsi chi ha preso i soldi si riparte dalla regola del corso
			...(isCourse && { paidTo: undefined }),
		})
	}

	// Scegliere un metodo vuol dire che ha pagato te. Nei corsi `paidTo` è
	// la regola del corso, e scegliere un metodo non la tocca.
	const handleSubstitutePaymentMethodChange = (absent, paymentMethod) =>
		updateSubstitute(absent, {
			paymentMethod,
			paymentAmount: paymentCoveredElsewhere(paymentMethod) ? 0 : substitutePrice,
			paymentAmountIsCustom: false,
			...(canSubstitutePaySchool && { paidTo: undefined }),
		})

	// Fuori dai corsi anche il sostituto può aver pagato il circolo: acceso,
	// un metodo non serve e si riparte dal listino; spento, il metodo resta
	// da scegliere.
	const handleSubstitutePaidToSchoolToggle = (absent, substitute) =>
		updateSubstitute(
			absent,
			isPaidToSchool(substitute.paidTo)
				? { paidTo: undefined, paymentMethod: null }
				: {
						paidTo: PAID_TO_SCHOOL,
						paymentMethod: "",
						paymentAmount: substitutePrice,
						paymentAmountIsCustom: false,
					},
		)

	const handleSubstitutePaymentAmountChange = (absent, amount) =>
		updateSubstitute(absent, {
			paymentAmount: amount,
			paymentAmountIsCustom: Number(amount) !== substitutePrice,
		})

	// Chi viene la volta al posto di un abbonato paga come in una privata con
	// gli allievi della lezione — anche dove la lezione si fattura al
	// circolo: lì non paga nessuno perché hanno tutti l'abbonamento, lui no.
	const substituteShowsPrice = substitute =>
		(Boolean(substitute?.paymentMethod) || paysSchool(substitute, canSubstitutePaySchool)) &&
		!paymentCoveredElsewhere(substitute.paymentMethod)

	const removeSubstitute = absent => setSubstitutes(prev => withSubstitute(prev, absent, null))

	const handlePartecipantsChange = (e, player) => {
		var newPartecipants = partecipants.slice()
		newPartecipants.splice(player, 1, {
			...withoutPaidTo(newPartecipants[player]),
			...recognizeTypedPlayer(e.target.value),
			// al SAT e al CAD si viene con l'abbonamento: chi paga in un altro
			// modo lo sceglie, e allora ricompare il prezzo
			paymentMethod: isCourse ? "subscription" : null,
			...(isCourse && { paymentAmount: 0, paymentAmountIsCustom: false }),
			// fuori dai corsi si riparte da a chi pagano di solito
			...(!isCourse && defaultPaidTo),
			...(isNew && { isPresent: true }),
		})
		setPartecipants(newPartecipants)
	}

	const handlePlayersInLessonPaymentAmountChange = (player, amount) => {
		var newPlayersInLesson = partecipants.slice()
		newPlayersInLesson.splice(player, 1, {
			...newPlayersInLesson[player],
			paymentAmount: amount,
			// un importo diverso dal listino è una scelta del maestro, e il
			// listino non deve più sovrascriverla; riscrivere il prezzo di
			// listino è il modo per tornare sui propri passi, visto che
			// ritoccare il metodo di pagamento già scelto non fa niente
			paymentAmountIsCustom: Number(amount) !== currentListPrice,
			...(isNew && { isPresent: true }),
		})
		setPartecipants(newPlayersInLesson)
	}

	// Scegliere come si paga riscrive l'importo, quindi azzera anche la
	// correzione: da lì si riparte dal listino. Un metodo vuol dire che ha
	// pagato te, non il circolo.
	const handlePlayersInLessonPaymentMethodChange = (player, paymentMethod) => {
		var newPlayersInLesson = partecipants.slice()
		const current = newPlayersInLesson[player]
		// nei corsi del circolo, lasciare l'abbonamento vuol dire pagare la
		// volta: chi ha preso i soldi resta quello detto, o la regola del corso
		const visit = canPayVisit && !paymentCoveredElsewhere(paymentMethod)
		newPlayersInLesson.splice(player, 1, {
			...withoutPaidTo(current),
			paymentMethod: paymentMethod,
			paymentAmount: paymentCoveredElsewhere(paymentMethod)
				? 0
				: visit
					? substitutePrice
					: currentListPrice,
			paymentAmountIsCustom: false,
			...(visit && { paidTo: current.paidTo ?? coursePaidTo }),
			// l'assenza è di chi ha l'abbonamento: chi paga la volta c'era
			...(paymentMethod !== "subscription" && { isPresent: true }),
		})
		setPartecipants(newPlayersInLesson)
	}

	// Ha pagato il circolo: un metodo non serve, e l'importo riparte dal
	// listino come scegliendo un metodo. Spento, il metodo resta da scegliere
	// e l'importo quello che era.
	const handlePaidToSchoolToggle = player => {
		var newPlayersInLesson = partecipants.slice()
		const current = newPlayersInLesson[player]
		newPlayersInLesson.splice(
			player,
			1,
			isPaidToSchool(current.paidTo)
				? { ...withoutPaidTo(current), paymentMethod: "" }
				: {
						...current,
						paidTo: PAID_TO_SCHOOL,
						paymentMethod: "",
						paymentAmount: currentListPrice,
						paymentAmountIsCustom: false,
						isPresent: true,
					},
		)
		setPartecipants(newPlayersInLesson)
	}

	// Cambiare chi paga il campo sposta dei soldi, non solo un'etichetta: a
	// ogni allievo si toglie la sua quota di campo, o gliela si rimette. Si
	// parte da quello che paga adesso e non dal listino, così funziona anche
	// su una lezione già salvata e sugli importi corretti a mano — che
	// restano corretti, solo senza il campo dentro.
	const handleCourtPaidByChange = nextCourtPaidBy => {
		setCourtPaidBy(nextCourtPaidBy)

		const nextShare = courtShareOf({
			lessonType,
			sportID,
			usingLights,
			usingHeat,
			court,
			courtPaidBy: nextCourtPaidBy,
			date: lessonDate,
			hours: currentLessonHours,
			players: totalPlayers,
		})

		// quanto passa di mano: positivo se il campo ora lo pagano loro
		const moved = nextShare - currentCourtShare
		if (moved === 0) return

		const nextListPrice = getPlayerPrice(currentFullPrice, nextShare)

		setPartecipants(prev =>
			prev.map(player => {
				if (paymentCoveredElsewhere(player.paymentMethod)) return player

				const amount = getPlayerPrice(
					savedPaymentAmount(player.paymentAmount, currentListPrice),
					moved,
				)

				return {
					...player,
					paymentAmount: amount,
					paymentAmountIsCustom: amount !== nextListPrice,
				}
			}),
		)
	}

	// La superficie del campo scelto: quella che dà il colore al pallino.
	const courtSurface = getSelectedSchool()?.courts?.[court?.courtsSport ?? sportID]?.[
		court?.courtNumber
	]

	// Quanto costa il campo, e basta: costa così che lo paghi tu o che lo
	// paghino loro. Quello che cambia — la quota tolta a ognuno — non è un
	// prezzo, ed è la riga qui sotto a dirlo.
	//
	// Su una lezione fatturata al circolo non si mostra: lì il campo non è
	// di nessuno dei due, e un prezzo senza padrone è solo una domanda.
	const courtMoney =
		currentCourtFee !== null ? `${formatAmount(currentCourtFee)} €/h` : null

	// Cosa succede agli importi, detto in una riga sotto la scelta. Il costo
	// del campo sta già lì accanto: qui va quello che non si vede, cioè cosa
	// ne è stato fatto.
	const courtPaidByHint = () => {
		if (isCourtPaidByPlayers(lessonCourtPaidBy)) {
			if (currentCourtShare > 0)
				return `Lo pagano loro al circolo: dagli importi sono già tolti ${formatAmount(currentCourtShare)} € a testa.`

			return "Lo pagano loro al circolo, ma senza una tariffa campo per questa data non c'è niente da togliere agli importi."
		}

		if (currentCourtFee !== null)
			return "Lo paghi tu: gli allievi pagano il prezzo pieno, e il campo si scala dal riepilogo."

		return null
	}

	// Quando giochi tu il campo lo paghi tu, tutto: si dice quanto, e dove
	// lo si ritrova.
	const matchCourtHint =
		courtMoney !== null
			? `Il campo lo paghi tu: ${courtMoney}. Nel riepilogo sta a parte, fuori dal netto.`
			: "Senza una tariffa campo per questa data, nel riepilogo non ha un costo."

	// Una lezione scelta dalla riga in cima. Quello che non le serve si
	// spegne, e cosa non le serve lo dice la tipologia; quello che le serve
	// e mancava — i giocatori, il campo, tornando da un centro estivo — si
	// rimette. Il numero di giocatori resta quello che era, finché ci sta.
	const selectLessonType = nextLessonType => {
		const nextType = getLessonType(nextLessonType)

		setLessonType(nextLessonType)

		if (lessonTypeHasPlayers(nextType)) {
			const { min, max } = playersRangeOf(nextLessonType)
			setTotalPlayers(Math.min(Math.max(totalPlayers, min), max))
		} else setTotalPlayers(0)

		if (!lessonTypeHasCourt(nextType)) {
			setCourt({ courtNumber: 0, courtsSport: sportID })
			setUsingLights(false)
			setUsingHeat(false)
			// senza campo non c'è nessun campo da pagare, né da scontare
			setCourtPaidBy(COURT_PAID_BY_COACH)
		} else if (Number(court?.courtNumber ?? court ?? 0) === 0) {
			const courtsSport = courtsSportOf(sportID)
			setCourt({
				courtNumber: Object.keys(getSelectedSchool().courts[courtsSport])[0],
				courtsSport,
			})
			// chi paga il campo torna quello che vale nel circolo
			setCourtPaidBy(schoolSettings.courtPaidBy)
		}

		// La durata proposta cambia solo se cambia quella del tipo: un'ora
		// e mezza scritta a mano non torna un'ora passando dal SAT al CAD.
		// E non passa la mezzanotte: se ci finirebbe, la lezione si sposta
		// prima, come quella proposta all'apertura.
		const nextDuration = getDefaultLessonDuration(nextType)
		if (isNew && nextDuration !== getDefaultLessonDuration(getLessonType(lessonType))) {
			const times = lessonTimesWithinDay(DateTime.fromSQL(lessonStartTime), nextDuration)
			setLessonStartTime(times.start)
			setLessonEndTime(times.end)
		}
	}

	// "Privata": quale delle private lo dice il numero di giocatori di adesso.
	const selectPrivateLesson = () => {
		const { min, max } = playersRangeOf(privateLessonTypes[0])
		const players = Math.min(Math.max(totalPlayers, min), max)
		const privateLessonType = privateLessonTypeFor(players, privateLessonTypes)
		if (privateLessonType) selectLessonType(privateLessonType)
	}

	// L'atletica non ha campi suoi: si fa su quelli di un altro sport, e
	// quello è il primo che il maestro insegna.
	const courtsSportOf = sport => (sport !== "atletica" ? sport : lessonCoach.taughtSports?.[0])

	// Cambiare sport riparte da capo: una privata da uno — o un SAT, per
	// l'atletica — sui campi di quello sport.
	const startClassicLesson = sport => {
		const courtsSport = courtsSportOf(sport)

		setSportID(sport)
		setLessonType(sport !== "atletica" ? "pri1" : "sat")
		setTotalPlayers(sport !== "atletica" ? 1 : getLessonType("sat").minimumPlayers)
		setCourt({
			courtNumber: Object.keys(getSelectedSchool().courts?.[courtsSport] ?? {})[0],
			courtsSport,
		})
	}

	const handleLessonNotesChange = e => {
		setLessonNotes(e.target.value)
	}

	const handleAssistantToggle = coachID => {
		setAssistantCoaches(prev => {
			if (prev.includes(coachID)) {
				return prev.filter(id => id !== coachID)
			}
			if (prev.length >= MAX_ASSISTANTS) return prev
			return [...prev, coachID]
		})
	}

	const getAvailableAssistants = () =>
		assignableCoaches.filter(
			c => c.coachID !== lessonCoach?.coachID && teachesInSchool(c, selectedSchool),
		)

	const isDisabled = () => {
		switch (formStep) {
			case 0:
				return (
					lessonDate === "" ||
					lessonStartTime === "" ||
					lessonEndTime === "" ||
					DateTime.fromISO(`${lessonDate}T${lessonStartTime}`) >=
						DateTime.fromISO(`${lessonDate}T${lessonEndTime}`)
				)
			// I nomi si possono segnare dopo, in qualunque lezione: un posto
			// lasciato vuoto resta un segnaposto finché non lo si riempie.
			default:
				return false
		}
	}

	useEffect(() => {
		if (isNew) {
			setLessonEndTime(
				DateTime.fromSQL(lessonStartTime)
					.plus({ hours: getDefaultLessonDuration(getLessonType(lessonType)) })
					.toFormat("HH:mm"),
			)
		} else {
			const lessonStart = DateTime.fromJSDate(lessonData.lessonStart.toDate())
			const lessonEnd = DateTime.fromJSDate(lessonData.lessonEnd.toDate())
			const lessonDuration = lessonEnd.diff(lessonStart, "hours").toObject().hours
			setLessonEndTime(
				DateTime.fromSQL(lessonStartTime).plus({ hours: lessonDuration }).toFormat("HH:mm"),
			)
		}
	}, [lessonStartTime])

	useEffect(() => {
		if (formStep < 2) {
			var partecipantsTmp = []
			Array(totalPlayers)
				.fill()
				.forEach((player, i) => {
					// un allievo tolto e rimesso ritrova quello che aveva
					const partecipant = partecipants[i] ?? (isNew ? null : savedPartecipants[i])

					// chi non ha ancora scelto come paga, in un corso paga con
					// l'abbonamento
					const courseDefault = isSubscriptionCourse(lessonType)
						? { paymentMethod: "subscription", paymentAmount: 0 }
						: null

					if (!partecipants[i]) {
						partecipantsTmp[i] = {
							paymentAmount: paymentAmountAfterEdit(partecipant, currentListPrice),
							paymentAmountIsCustom: partecipant?.paymentAmountIsCustom ?? false,
							paymentMethod: "",
							...(courseDefault ?? defaultPaidTo),
							playerName: "",
							playerID: "",
							isPresent: true,
						}
					} else {
						partecipantsTmp[i] = {
							paymentAmount: paymentAmountAfterEdit(
								partecipant,
								listPriceFor(partecipant),
							),
							paymentAmountIsCustom: partecipant.paymentAmountIsCustom ?? false,
							paymentMethod: partecipant.paymentMethod,
							// chi ha pagato il circolo un metodo non ce l'ha, e
							// non per questo ha l'abbonamento; chi nel corso paga
							// la volta si tiene chi ha preso i soldi
							...(paysVisit(partecipant)
								? { paidTo: partecipant.paidTo ?? coursePaidTo }
								: isPaidToSchool(partecipant.paidTo)
									? { paidTo: partecipant.paidTo }
									: !partecipant.paymentMethod && courseDefault),
							playerName: partecipant.playerName,
							playerID: partecipant.playerID,
							isPresent: partecipant.isPresent,
							// un'assenza recuperata altrove resta com'era
							...(partecipant.recoveredElsewhere === true && {
								recoveredElsewhere: true,
							}),
						}
					}
				})
			setPartecipants(partecipantsTmp)

			// Nella privata il numero di giocatori dice quale privata: cambiare
			// il numero — anche togliendo un giocatore da una lezione salvata —
			// cambia la privata. Le altre tipologie restano quelle che sono.
			if (isPrivate) {
				const sizedLessonType = privateLessonTypeFor(totalPlayers, privateLessonTypes)
				if (sizedLessonType && sizedLessonType !== lessonType) setLessonType(sizedLessonType)
			}

			// Una lezione nuova parte da una privata da uno: se qui quella non
			// si fa, si parte dalla prima che si fa.
			if (isNew && hasPlayers && !playerLessonTypesHere.includes(lessonType)) {
				if (privateLessonTypes.length > 0) selectPrivateLesson()
				else if (courseLessonTypes.length > 0) selectLessonType(courseLessonTypes[0])
			}
		}
	}, [totalPlayers, lessonType, formStep, sportID])

	// Cambiando giorno, la ricorrenza riparte da una settimana dopo. Non
	// all'apertura, quando vale già così: una serie da reinserire arriva con
	// la sua ultima data, e la perderebbe.
	const recurrenceFromDate = useRef(lessonDate)
	useEffect(() => {
		if (recurrenceFromDate.current === lessonDate) return
		recurrenceFromDate.current = lessonDate
		setRecurrentLessonDateEnd(
			DateTime.fromISO(lessonDate).plus({ week: 1 }).toISO().split("T")[0],
		)
	}, [lessonDate])

	// Un collega non ancora caricato non dice che sport insegna: lo sport
	// della lezione resta quello che è. Una lezione nuova che cambia sport
	// cambia anche campo, come toccando lo sport in cima: passando da una
	// collega che insegna padel a sé, che insegni tennis, il campo di padel
	// non resta lì.
	useEffect(() => {
		const taughtSports = lessonCoach?.taughtSports
		if (!taughtSports?.length || taughtSports.includes(sportID)) return
		if (isNew) startClassicLesson(taughtSports[0])
		else setSportID(taughtSports[0])
	}, [lessonCoach])

	// Un salvataggio che fallisce lasciava l'utente davanti a una schermata
	// muta: l'errore finiva in console e basta. Ora si dice cosa è andato
	// storto e si torna al modulo, con quello che si era scritto ancora lì.
	const reportSubmitError = message => error => {
		console.error(message, error)
		// senza rete si dice che serve la rete, non che "non è stato possibile"
		const shown = networkAwareMessage(error, message)
		toast.error(shown, { toastId: shown, position: "top-center" })
	}

	// Un posto lasciato senza nome non è un allievo, e quindi nemmeno un
	// pagamento: si salva a zero, col prezzo di listino da riprendere quando
	// il nome arriva. Non è una correzione del maestro, e va detto: se no
	// riaprendo la lezione quello zero sembrerebbe un prezzo voluto.
	//
	// In una lezione fatturata al circolo nessuno paga niente, e nemmeno il
	// circolo: chi l'aveva scelto quando la lezione era un'altra lo perde.
	const partecipantsToSave = partecipants
		.map(player =>
			isPlaceholderPartecipant(player)
				? { ...player, paymentAmount: 0, paymentAmountIsCustom: false }
				: player,
		)
		.map(player => (canPaySchool || paysVisit(player) ? player : withoutPaidTo(player)))

	const handleSubmit = () => {
		setIsSubmitting(true)
		// Il titolo dell'evento su Google lo fa chi salva, dagli allievi
		// salvati (`buildEventTitle`): è lo stesso che rifà il controllo del
		// calendario, e i due devono coincidere. Da qui serve solo il nome.
		const lessonTypeName = getLessonType(lessonType).lessonTypeName

		if (isEdit) {
			var paymentAmountByMethod = getPaymentAmountByType(partecipants)
			var playersList = partecipants.reduce((players, player) => {
				if (partecipants[0].playerName !== player.player) players += `, ${player?.player}`
				return players
			}, partecipants[0]?.playerName)

			var editedLessonData = {
				lessonDate: lessonDate,
				lessonStart: Timestamp.fromDate(new Date(`${lessonDate}T${lessonStartTime}`)),
				lessonEnd: Timestamp.fromDate(new Date(`${lessonDate}T${lessonEndTime}`)),
				lessonType: lessonType,
				players: playersList,
				cashPaymentAmount: paymentAmountByMethod.cash,
				satispayPaymentAmount: paymentAmountByMethod.satispay,
				subscriptionCount: paymentAmountByMethod.subscription,
				posPaymentAmount: paymentAmountByMethod.pos,
				bankTransferPaymentAmount: paymentAmountByMethod.bankTransfer,
				lessonNotes: lessonNotes,
				satLevel: satLevelToSave(lessonType, satLevel),
				ptrLevel: ptrLevelToSave(lessonType, ptrLevel),
				isRecurrent: false,
				recurrentLessonDateEnd: "",
				partecipants: [
					...partecipantsToSave,
					...keptSubstitutes(
						substitutes.filter(substitute => substitute.playerName?.trim()),
						partecipants,
					).map(substitute => ({
						...substitute,
						// chi l'ha incassato resta quello di quando lo si è segnato
						paidTo:
							substitute.paidTo ??
							substitutePaidToFor(lessonType, getLessonType(lessonType)),
					})),
				],
				court: court,
				usingLights: usingLights,
				usingHeat: usingHeat,
				courtPaidBy: lessonCourtPaidBy,
				billedTo: billedTo,
				lessonID: lessonData?.lessonID,
				recurrencyID: lessonData?.recurrencyID,
				coachID: lessonCoach?.coachID,
				assistantCoachIDs: lessonAssistants.length > 0 ? lessonAssistants : null,
				schoolID: getSelectedSchool().schoolID,
				sportID: lessonSportID,
				eventID: lessonData?.eventID,
				lessonTypeName: lessonTypeName,
				knownPlayers: activePlayers,
			}

			editLesson(editedLessonData, editSeries)
				.then(editedLesson => {
					console.info("Lezione modificata", editedLesson)
					const originalDate = lessonData.lessonStart.toDate().toISOString().split("T")[0]
					if (lessonDate !== originalDate) {
						const originalSchoolID =
							lessonData.schoolID ?? getSelectedSchool()?.schoolID
						removeLessonFromCache({
							schoolID: originalSchoolID,
							date: originalDate,
							coachID: lessonData.coachID,
							lessonID: lessonData.lessonID,
						})
						invalidateDate(originalDate, originalSchoolID)
					}
					closeModal()
				})
				.catch(reportSubmitError("Non è stato possibile modificare la lezione"))
				.finally(() => setIsSubmitting(false))
		} else {
			const addRecurrent = async newLessonData => {
				var initialLessonDate = newLessonData.lessonStart.toDate()
				var finalLessonDate = new Date(`${recurrentLessonDateEnd}T${lessonEndTime}`)
				const promises = []
				for (
					var dateIndex = initialLessonDate;
					dateIndex.getTime() <= finalLessonDate.getTime();
					dateIndex.setDate(dateIndex.getDate() + 7)
				) {
					const start = new Date(dateIndex)
					const end = new Date(
						`${DateTime.fromJSDate(start).toFormat("yyyy-MM-dd")}T${lessonEndTime}`,
					)
					promises.push(
						addLesson({
							...newLessonData,
							lessonStart: Timestamp.fromDate(start),
							lessonEnd: Timestamp.fromDate(end),
						}),
					)
				}

				// Una lezione che non passa non cancella quelle che sono
				// passate: prima bastava lei a far dire che le lezioni non
				// erano state create, mentre in calendario c'erano tutte le
				// altre — e chi ci credeva le rifaceva tutte.
				const results = await Promise.allSettled(promises)
				const created = results.filter(result => result.status === "fulfilled")
				const failed = results.filter(result => result.status === "rejected")

				if (failed.length === 0) {
					console.info("Lezioni ricorrenti create:", created.length)
					closeModal()
					return
				}

				failed.forEach(result => console.error("Lezione ricorrente", result.reason))

				if (created.length === 0) {
					toast.error("Non è stato possibile creare le lezioni", {
						toastId: "recurrent-lessons-failed",
						position: "top-center",
					})
					return
				}

				// Il modulo si chiude lo stesso: riaprirlo così com'è rifarebbe
				// anche le lezioni che ci sono già, e le date mancanti si
				// aggiungono una per una da qui.
				toast.warning(
					`Create ${created.length} lezioni su ${results.length}: le altre vanno rifatte`,
					{ toastId: "recurrent-lessons-partial", position: "top-center" },
				)
				closeModal()
			}

			const addRecurrentOrSingle = async lessonDataToAdd => {
				try {
					// passo gli allievi già in memoria: se il nome è scritto a mano
					// va riconosciuto invece di creare un doppione
					const knownLessonData = { ...lessonDataToAdd, knownPlayers: activePlayers }
					// e li risolvo qui, una volta sola per tutta la serie: le
					// lezioni partono insieme, quindi un nome nuovo non lo
					// troverebbe nessuna — non l'ha ancora creato nessuno — e
					// ognuna se ne creerebbe uno suo, un doppione per lezione
					const newLessonData = {
						...knownLessonData,
						partecipants: await resolveLessonPartecipants(knownLessonData),
					}

					if (isRecurrent) {
						await addRecurrent(newLessonData)
					} else {
						const newLesson = await addLesson(newLessonData)
						console.info("Lezione creata", newLesson)
						// la lezione annullata si ricorda del suo recupero: chi
						// la ripristina deve poter dire che fine fa
						if (isReschedule && lessonData?.cancelledLessonID) {
							linkRescheduledLesson(lessonData.cancelledLessonID, newLesson.id).catch(
								error => console.error("Recupero non collegato:", error),
							)
						}
						closeModal()
					}
				} catch (error) {
					const message = isRecurrent
						? "Non è stato possibile creare le lezioni"
						: "Non è stato possibile creare la lezione"
					reportSubmitError(message)(error)
				} finally {
					setIsSubmitting(false)
				}
			}

			if (isNew) {
				addRecurrentOrSingle({
					lessonStart: Timestamp.fromDate(new Date(`${lessonDate}T${lessonStartTime}`)),
					lessonEnd: Timestamp.fromDate(new Date(`${lessonDate}T${lessonEndTime}`)),
					lessonType: lessonType,
					lessonNotes: lessonNotes,
					satLevel: satLevelToSave(lessonType, satLevel),
					ptrLevel: ptrLevelToSave(lessonType, ptrLevel),
					recurrencyID: isRecurrent ? uuidv4() : null,
					recurrentLessonDateEnd: recurrentLessonDateEnd,
					partecipants: partecipantsToSave,
					court: court,
					usingLights: usingLights,
					usingHeat: usingHeat,
					courtPaidBy: lessonCourtPaidBy,
					billedTo: billedTo,
					coachID: lessonCoach?.coachID,
					assistantCoachIDs: lessonAssistants.length > 0 ? lessonAssistants : null,
					schoolID: getSelectedSchool().schoolID,
					sportID: lessonSportID,
					createdBy: coach.coachID,
					lessonTypeName: lessonTypeName,
				})
			} else {
				var paymentAmountByType = getPaymentAmountByType(partecipants)
				var playersList = partecipants.reduce((players, player) => {
					if (partecipants[0].playerName !== player.player)
						players += `, ${player?.player}`
					return players
				}, partecipants[0]?.playerName)
				addRecurrentOrSingle({
					lessonDate: lessonDate,
					lessonStart: Timestamp.fromDate(new Date(`${lessonDate}T${lessonStartTime}`)),
					lessonEnd: Timestamp.fromDate(new Date(`${lessonDate}T${lessonEndTime}`)),
					lessonType: lessonType,
					players: playersList,
					cashPaymentAmount: paymentAmountByType.cash,
					satispayPaymentAmount: paymentAmountByType.satispay,
					subscriptionCount: paymentAmountByType.subscription,
					posPaymentAmount: paymentAmountByType.pos,
					bankTransferPaymentAmount: paymentAmountByType.bankTransfer,
					lessonNotes: lessonNotes,
					satLevel: satLevelToSave(lessonType, satLevel),
					ptrLevel: ptrLevelToSave(lessonType, ptrLevel),
					isRecurrent: isRecurrent,
					recurrencyID: isRecurrent ? uuidv4() : null,
					recurrentLessonDateEnd: recurrentLessonDateEnd,
					partecipants: partecipantsToSave,
					court: court,
					usingLights: usingLights,
					usingHeat: usingHeat,
					courtPaidBy: lessonCourtPaidBy,
					billedTo: billedTo,
					coachID: lessonCoach.coachID,
					assistantCoachIDs: lessonAssistants.length > 0 ? lessonAssistants : null,
					schoolID: getSelectedSchool().schoolID,
					sportID: lessonSportID,
					createdBy: coach.coachID,
					lessonTypeName: lessonTypeName,
				})
			}
		}
	}

	const lessonTitle = isNew
		? "Nuova lezione"
		: isEdit
			? "Modifica lezione"
			: isReschedule
				? "Riprogramma lezione"
				: isReinsert
					? reinsertUntil
						? "Reinserisci serie"
						: "Reinserisci lezione"
					: "Duplica lezione"
	const matchTitle = isNew
		? "Gioco io"
		: isEdit
			? "Modifica · Gioco io"
			: isReinsert
				? "Reinserisci · Gioco io"
				: "Duplica · Gioco io"
	const title = isMatchForm ? matchTitle : lessonTitle
	// Ricorrenza settimanale e modifica della serie: per le lezioni normali
	// stanno nello step di riepilogo, per i tipi speciali nel primo step,
	// visto che quello di riepilogo non lo vedono
	const recurrenceFields = (
		<>
			{/* Edit: radio per modifica serie */}
			{isEdit && lessonData?.recurrencyID && (
				<>
					<Text m={{ b: "0.5rem" }}>
						Questa è una lezione ricorrente: è possibile modificare solo questa o
						anche le successive, come preferisci fare?
					</Text>
					<RadioBox
						text="Modifica solo questa"
						checked={editSeries === false}
						handleChange={() => setEditSeries(false)}
					/>
					<RadioBox
						text="Modifica anche le prossime"
						checked={editSeries === true}
						handleChange={() => setEditSeries(true)}
					/>
					{editSeries && !online && (
						<Text textColor={theme.colors.red} m={{ t: "0.5rem" }}>
							Senza rete si può modificare solo questa lezione
						</Text>
					)}
				</>
			)}

			{/* New e Duplicate: toggle ricorrenza. Il recupero di una lezione
			    annullata è uno, e non si ripete */}
			{!isEdit && !isReschedule && (
				<>
					<Label
						onClick={() => setLessonRecurrent(!isRecurrent)}
						display="flex"
						justify="space-between"
						align="center"
						m={{ y: "1rem" }}>
						<Text textWeight="bold" textSize="subheader">
							{isMatchForm ? "Ripeti ogni settimana" : "Ripeti lezione"}
						</Text>
						<Switch
							m={{ l: "0.5rem" }}
							shadow="0"
							checked={isRecurrent}
							activeColor={typeAccent}
							inactiveColor="gray_light"
						/>
					</Label>
					{isRecurrent && (
						<Paragraph
							title={`Dal ${DateTime.fromISO(lessonDate).toFormat("dd/MM/yyyy")} fino al`}>
							<DateInput
								value={recurrentLessonDateEnd}
								handleChange={setRecurrentLessonDateEnd}
								step="7"
								min={
									DateTime.fromISO(lessonDate)
										.plus({ week: 1 })
										.toISO()
										.split("T")[0]
								}
							/>
						</Paragraph>
					)}
				</>
			)}
		</>
	)
	// Senza nomi da segnare non c'è uno step dei partecipanti, e quindi
	// nemmeno un riepilogo: si conferma dal primo.
	const isConfirmStep = formStep === lastFormStep || !hasPlayers

	// Mentre si salva il modulo sparisce, ma lo step resta quello che era:
	// prima si avanzava a uno step che non esiste, e se il salvataggio non
	// arrivava mai — o falliva — si restava lì, davanti al nulla. Così se
	// qualcosa va storto si ritrova il riepilogo esattamente com'era.
	const visibleStep = isSubmitting ? null : formStep

	// ──────────────────────────────────────────────────────────────────
	// PER CHI È LA LEZIONE
	//
	// Il selettore lo vede chi guarda le lezioni dei colleghi, e ci trova
	// tutti i maestri del circolo: quelli per cui non può inserire lezioni
	// hanno un lucchetto. Sceglierne uno — o partire da uno, perché è quello
	// che si stava guardando — stende un velo sul modulo che dice perché non
	// si va avanti, e offre di inserirla per sé.
	//
	// È chiarezza, non un permesso: le regole di Firestore una lezione
	// intestata a chi non si gestisce la rifiutano comunque.
	//
	// Modificando una lezione non cambia niente: si sceglie tra chi si può.
	// ──────────────────────────────────────────────────────────────────
	const seesColleagues = getViewableCoaches(coach, getSelectedSchool(), selectedSchool).some(
		viewableCoach => viewableCoach.coachID !== coach?.coachID,
	)
	// Quando giochi tu non c'è un maestro da scegliere: sei tu.
	const coachSelectorVisible =
		!isMatchForm &&
		(isEdit ? assignableCoaches.length > 1 : seesColleagues || assignableCoaches.length > 1)
	const canAssignTo = coachID => canAssignLessonTo(coach, selectedSchool, coachID)
	const lessonCoachLocked = !isEdit && !canAssignTo(lessonCoach?.coachID)
	// gli assistenti di una lezione che non si può inserire non servono
	const assistantsSelectorVisible =
		coachSelectorVisible && assistantsAllowed && !lessonCoachLocked

	const selectLessonCoach = selectedCoach => {
		setLessonCoach(selectedCoach)
		setAssistantCoaches([])
		setShowAssistants(false)
		setDropdownOpenState(false)
	}

	// ──────────────────────────────────────────────────────────────────
	// DUE RIGHE IN CIMA, E OGNUNA DICE UNA COSA SOLA
	//
	// Sopra gli sport, e solo a chi ne insegna più di uno: una scelta con
	// una risposta sola non è una scelta.
	//
	// Sotto che lezione è, e la si sceglie solo da lì: "Privata", il SAT, il
	// CAD, le tipologie del circolo, e poi quelle senza nomi da segnare — il
	// centro estivo, le racchette in classe. Prima le lezioni coi giocatori
	// si sceglievano da un elenco sotto il numero di giocatori, che cambiava
	// a ogni più e meno, e la riga in cima aveva solo "Classica" accanto alle
	// altre: due posti per scegliere la stessa cosa.
	//
	// La riga è uno specchio del listino: una tipologia che togli di là
	// sparisce anche di qua.
	// ──────────────────────────────────────────────────────────────────
	const taughtSports = lessonCoach?.taughtSports ?? []

	const standaloneLessonTypes = lessonTypesHere.filter(
		lessonTypeID => !lessonTypeHasPlayers(lessonTypes[lessonTypeID]),
	)

	const schoolCourtSports = Object.keys(getSelectedSchool()?.courts ?? {})

	// Duplicando non si cambia né sport né tipo: si rifà la stessa lezione.
	// Modificandone una si può cambiare sport e tipo, ma non farla diventare
	// un centro estivo — quella sarebbe un'altra lezione.
	// E quando giochi tu non è una lezione: dalla riga non si sceglie, e
	// non ci si torna.
	const tabsVisible =
		!isDuplicate && !isMatchForm && visibleStep === 0 && (isNew || hasPlayers)
	const sportTabsVisible = tabsVisible && taughtSports.length > 1

	const lessonTypeTabs = [
		...(privateLessonTypes.length > 0
			? [
					{
						key: "privata",
						text: PRIVATE_LESSON_LABEL,
						isSelected: isPrivate,
						color: getLessonFillColor(getLessonType(privateLessonTypes[0])),
						onClick: () => {
							if (!isPrivate) selectPrivateLesson()
						},
					},
				]
			: []),
		...[...courseLessonTypes, ...(isNew ? standaloneLessonTypes : [])].map(lessonTypeID => ({
			key: lessonTypeID,
			text: lessonTypeDisplayNameOf(getLessonType(lessonTypeID), lessonTypeID),
			isSelected: lessonType === lessonTypeID,
			color: getLessonFillColor(getLessonType(lessonTypeID)),
			onClick: () => {
				if (lessonType !== lessonTypeID) selectLessonType(lessonTypeID)
			},
		})),
	]
	const lessonTypeTabsVisible = tabsVisible && lessonTypeTabs.length > 1

	// Una pastiglia delle righe in cima: stesso aspetto sopra e sotto, perché
	// sono due scelte dello stesso peso. Le tipologie, scelte, prendono il
	// pieno del loro tipo — lo stesso di "Avanti" e dei più e meno — così si
	// vede subito che lezione si sta mettendo in calendario.
	const tabChip = ({ key, text, isSelected, onClick, color = theme.colors.primary }) => (
		<Div
			key={key}
			d="flex"
			align="center"
			p={"0.25rem 0.75rem"}
			w="fit-content"
			rounded="15px"
			style={{ gap: "0.5rem", cursor: "pointer" }}
			border="2px solid"
			borderColor={isSelected ? color : theme.colors.surface}
			bg={isSelected ? color : theme.colors.background}
			transition
			onClick={onClick}>
			<Text textWeight={isSelected && "bold"} textColor={isSelected && theme.colors.white}>
				<nobr>{text}</nobr>
			</Text>
		</Div>
	)

	const tabsRow = children => (
		<Div
			d="flex"
			align="center"
			m={{ b: "0.5rem", l: "0.75rem" }}
			style={{ gap: "0.5rem", overflow: "auto" }}>
			{children}
		</Div>
	)

	// Il livello del SAT: facoltativo, e toccare quello già scelto lo toglie.
	const satLevelPicker = (
		<Paragraph title="Livello (facoltativo):">
			<Div d="flex" flexWrap="wrap" m={{ t: "0.5rem" }} style={{ gap: "0.5rem" }}>
				{SAT_LEVELS.map(level => {
					const isSelected = satLevel === level.value

					return (
						<motion.div key={level.value} whileTap={{ scale: 0.9 }}>
							<Div
								d="flex"
								align="center"
								p="0.25rem 0.75rem"
								rounded="15px"
								style={{ gap: "0.5rem", cursor: "pointer" }}
								border="2px solid"
								borderColor={
									isSelected ? theme.colors[level.color] : theme.colors.surface
								}
								bg={theme.colors.background}
								transition
								onClick={() => setSatLevel(isSelected ? null : level.value)}>
								<Div
									w="0.9rem"
									h="0.9rem"
									rounded="circle"
									bg={theme.colors[level.color]}
								/>
								<Text textWeight={isSelected && "bold"}>{level.label}</Text>
							</Div>
						</motion.div>
					)
				})}
			</Div>
		</Paragraph>
	)

	// Il livello come finirà sulla lezione, per il riepilogo: solo nel SAT o
	// nel CAD, e solo se lo si è scelto.
	const savedSatLevel = getSatLevel(satLevelToSave(lessonType, satLevel))
	const savedPtrLevel = getPtrLevel(ptrLevelToSave(lessonType, ptrLevel))

	const coachMenuItems = isEdit
		? assignableCoaches
		: getSchoolCoaches(coach, getSelectedSchool(), selectedSchool)

	return (
		<>
			<Container>
				<Div
					d={"flex"}
					justify="space-between"
					align="center"
					m={{ b: "0.5rem" }}
					textWeight="bold"
					textSize="title"
					textColor={theme === "light" ? "text" : "text_alt"}>
					<Text textSize="title">{title}</Text>
					<Div>
						<Div d="flex" align="center">
							{coachSelectorVisible && (
								<Dropdown
									w="fit-content"
									h="2.5rem"
									p="0.25rem 0.5rem"
									rounded={
										assistantsSelectorVisible
											? theme.rounded["l-20px"]
											: theme.rounded["20px"]
									}
									bg="surface"
									focusBg="surface"
									border="3px solid"
									direction="bottomright"
									borderColor={theme.colors.surface}
									focusBorderColor={theme.colors.gunmetal}
									textColor={"black"}
									isOpen={dropdownIsOpen}
									onClick={() => setDropdownOpenState(!dropdownIsOpen)}
									menu={coachMenuItems.map((displayedCoach, i) => (
										<>
											<Text
												key={displayedCoach.coachID}
												d="flex"
												align="center"
												p="0.75rem 1rem"
												textWeight="normal"
												style={{ gap: "0.6rem" }}
												onClick={() => selectLessonCoach(displayedCoach)}>
												<CoachAvatar
													coach={coachByID(displayedCoach.coachID)}
													size={32}
												/>
												<span>{coachNameOf(displayedCoach.coachID)}</span>
												{!canAssignTo(displayedCoach.coachID) && (
													<FontAwesomeIcon
														icon={faLock}
														size="xs"
														aria-label="Non puoi inserire lezioni per questo maestro"
													/>
												)}
											</Text>
											{i < coachMenuItems.length - 1 && (
												<Divider
													m={{ x: "0.5rem" }}
													w="2px solid"
													borderColor={theme.colors.background}
												/>
											)}
										</>
									))}>
									<Div
										w="fit-content"
										d="flex"
										align="center"
										style={{ gap: "0.5rem" }}
										textWeight="normal"
										bg={theme.colors.surface}>
										{/* chi fa la lezione, e gli assistenti dietro di
										    lui, un tondo sull'altro */}
										<Div d="flex" style={{ flexShrink: 0 }}>
											{[lessonCoach.coachID, ...lessonAssistants].map(
												(coachID, i) => (
													<CoachAvatar
														key={coachID}
														coach={coachByID(coachID)}
														size={28}
														style={{
															marginLeft: i > 0 ? "-0.6rem" : 0,
															boxShadow: `0 0 0 2px ${theme.colors.surface}`,
														}}
													/>
												),
											)}
										</Div>
										<Div
											maxW="25vw"
											overflow="hidden"
											whiteSpace="nowrap"
											style={{
												overflow: "hidden",
												whiteSpace: "nowrap",
												textOverflow: "ellipsis",
											}}>
											<Text tag="span">
												{lessonAssistants.length === 0 ||
												lessonCoach.coachID === coach.coachID
													? coachNameOf(lessonCoach.coachID)
													: coachNameOf(lessonCoach.coachID).split(" ")[0]}
											</Text>
											<>
												{lessonAssistants.map(assistantCoach => (
													<Text key={assistantCoach} tag="span">
														,{" "}
														{coachNameOf(assistantCoach).split(" ")[0]}
													</Text>
												))}
											</>
										</Div>
									</Div>
								</Dropdown>
							)}
							{assistantsSelectorVisible && (
								<Dropdown
									w="fit-content"
									h="2.5rem"
									p="0.25rem 0.5rem"
									m={{ l: "0.25rem" }}
									rounded={theme.rounded["r-20px"]}
									bg="surface"
									focusBg="surface"
									border="3px solid"
									direction="bottomright"
									borderColor={theme.colors.surface}
									focusBorderColor={theme.colors.gunmetal}
									textColor={"black"}
									isOpen={showAssistants}
									openSuffix={<Icon name="UpArrow" color="black" size="16px" />}
									closeSuffix={<FontAwesomeIcon icon={faPlus} size="l" />}
									onClick={() => setShowAssistants(!showAssistants)}
									title={
										<Text p="0.5rem 1rem 0" textSize="subheader">
											Assistenti:
										</Text>
									}
									menu={getAvailableAssistants().map((availableCoach, i) => (
										<>
											<Label
												align="center"
												textWeight="normal"
												key={availableCoach.coachID}
												p="0.75rem 1rem">
												<Checkbox
													onChange={() =>
														handleAssistantToggle(
															availableCoach.coachID,
														)
													}
													checked={assistantCoaches.includes(
														availableCoach.coachID,
													)}
													inactiveColor={theme.colors.gunmetal_lighter}
													activeColor={theme.colors.gunmetal}
												/>
												{availableCoach.coachName}
											</Label>
											{i < getAvailableAssistants().length - 1 && (
												<Divider
													m={{ x: "0.5rem" }}
													w="2px solid"
													borderColor={theme.colors.background}
												/>
											)}
										</>
									))}></Dropdown>
							)}
						</Div>
					</Div>
				</Div>
			</Container>

			{/* Il velo copre il modulo, non il selettore qui sopra: da lì se ne
			    sceglie un altro */}
			<Div pos="relative">
				{lessonCoachLocked && (
					<Div
						pos="absolute"
						top="0"
						left="0"
						right="0"
						bottom="0"
						zIndex="5"
						d="flex"
						flexDir="column"
						align="center"
						justify="center"
						p="1.5rem"
						bg={theme.colors.background_blur}
						style={{ backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
						role="alert">
						<FontAwesomeIcon icon={faLock} size="lg" />
						<Text m={{ y: "1rem" }} textAlign="center" textSize="subheader">
							Non hai i permessi per inserire lezioni per{" "}
							<Text tag="b" textWeight="bold">
								{coachNameOf(lessonCoach.coachID).split(" ")[0]}
							</Text>
							.
						</Text>
						{/* chi qui non insegna lezioni sue non ne ha */}
						{teachesInSchool(coach, selectedSchool) && (
							<PrimaryButton
								bg={theme.colors.gunmetal_fill}
								textColor={theme.colors.white}
								textSize="subheader"
								text="Inseriscila per te"
								action={() => selectLessonCoach(coach)}
							/>
						)}
					</Div>
				)}
				{(sportTabsVisible || lessonTypeTabsVisible) && (
					<>
						{/* Gli sport: solo a chi ne insegna più di uno */}
						{sportTabsVisible &&
							tabsRow(
								taughtSports.map(sport => {
									const isSelected = sportID === sport && hasPlayers

									return tabChip({
										key: sport,
										text: getSport(sport).sportName,
										isSelected,
										// ritoccare lo sport già scelto rimetterebbe
										// a zero giocatori e tipo: la pastiglia
										// attiva non fa niente
										onClick: () => {
											if (!isSelected) startClassicLesson(sport)
										},
									})
								}),
							)}

						{/* Che lezione è: la privata, i corsi, e quelle senza nomi */}
						{lessonTypeTabsVisible && tabsRow(lessonTypeTabs.map(tabChip))}

						<Divider w="2px solid" borderColor={"surface"} />
					</>
				)}

				<Div maxH="60vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
					{isSubmitting && (
						<Container>
							<Text m={{ y: "1rem" }} textAlign="center" textSize="subheader">
								{isEdit
									? "Sto salvando la modifica e aggiornando l'evento su Google Calendar."
									: isMatchForm
										? "Sto salvando e creando l'evento su Google Calendar."
										: "Sto salvando la lezione e creando l'evento su Google Calendar."}
							</Text>
						</Container>
					)}

					{/* Step 0 */}
					{visibleStep === 0 && (
						<Container>
							<motion.div
								initial={{ opacity: 0, y: 5 }}
								animate={{ opacity: 1, x: 0 }}
								exit={{ opacity: 0 }}>
								<Row>
									<Col size={{ xs: 12, lg: 4 }}>
										<Paragraph
											title={isMatchForm ? "Quando giochi:" : "Data della lezione:"}>
											<DateInput
												value={lessonDate}
												handleChange={setLessonDate}
											/>
										</Paragraph>
									</Col>
									<Col size={{ xs: 6, lg: 4 }}>
										<Paragraph title="Ora di inizio:">
											<TimeInput
												value={lessonStartTime}
												handleChange={setLessonStartTime}
											/>
										</Paragraph>
									</Col>
									<Col size={{ xs: 6, lg: 4 }}>
										<Paragraph title="Ora di fine:">
											<TimeInput
												value={lessonEndTime}
												handleChange={setLessonEndTime}
											/>
										</Paragraph>
									</Col>
								</Row>
								{/* a quest'ora il maestro ha già una lezione, il campo è
								    preso, o si è in un altro circolo? Si dice, e basta */}
								<LessonOverlapWarning
									date={lessonDate}
									startTime={lessonStartTime}
									endTime={lessonEndTime}
									lessonCoach={lessonCoach}
									court={hasCourt ? court : null}
									lessonID={isEdit ? lessonData.lessonID : null}
								/>
								<Divider w="2px solid" borderColor={"surface"} />

								{/* Quanti sono: solo dove i nomi si segnano. Che
								    lezione è lo si sceglie dalla riga in cima */}
								{hasPlayers && (
									<>
										<Paragraph title={`Numero di giocatori:`}>
											<Div
												d="flex"
												align="center"
												justify={isSmartphone ? "center" : "start"}
												w="100%"
												m={{ y: "1rem" }}>
												<Button
													bg={typeAccent}
													h="3rem"
													w="3rem"
													m={{ x: "1rem" }}
													rounded="circle"
													textColor="white"
													aria-label="Un giocatore in meno"
													disabled={nextPlayersCount(-1) === undefined}
													onClick={() => {
														const next = nextPlayersCount(-1)
														if (next !== undefined) setTotalPlayers(next)
													}}>
													<FontAwesomeIcon
														icon={faMinus}
														size="2x"
														color="white"
													/>
												</Button>
												<Text textSize="display1" m={{ x: "1rem" }}>
													{totalPlayers}
												</Text>
												<Button
													bg={typeAccent}
													h="3rem"
													w="3rem"
													m={{ x: "1rem" }}
													rounded="circle"
													textColor="white"
													aria-label="Un giocatore in più"
													disabled={nextPlayersCount(1) === undefined}
													onClick={() => {
														const next = nextPlayersCount(1)
														if (next !== undefined) setTotalPlayers(next)
													}}>
													<FontAwesomeIcon
														icon={faPlus}
														size="2x"
														color="white"
													/>
												</Button>
											</Div>
										</Paragraph>
										{lessonTypeHasSatLevel(lessonType) && satLevelPicker}
										{lessonTypeHasPtrLevel(lessonType) && (
											<PtrLevelPicker value={ptrLevel} onChange={setPtrLevel} />
										)}
									</>
								)}

								{/* Il campo è una domanda a parte: c'è chi segna i
								    nomi senza occupare un campo, e chi occupa un
								    campo senza segnare i nomi */}
								{hasCourt && (
									<>
										{hasPlayers && <Divider w="2px solid" borderColor={"surface"} />}
										<Paragraph title={`Campo:`}>
											{/* i campi di un altro sport: dove non insegni
											    quello della lezione, o quando giochi tu,
											    che si gioca dove si vuole */}
											{(!schoolCourtSports.includes(sportID) ||
												(isMatchForm && schoolCourtSports.length > 1)) && (
												<Row
													m={{ t: "0.5rem", b: "1rem" }}
													style={{ gap: "0.5rem" }}>
													{Object.keys(getSelectedSchool().courts).map(
														sport => (
															<Div
																key={sport}
																d="flex"
																align="center"
																p={"0.25rem 0.75rem"}
																w="fit-content"
																rounded="15px"
																style={{
																	gap: "0.5rem",
																	cursor: "pointer",
																}}
																border="2px solid"
																borderColor={
																	court.courtsSport === sport
																		? theme.colors.gunmetal_fill
																		: theme.colors.surface
																}
																bg={
																	court.courtsSport === sport
																		? theme.colors.gunmetal_fill
																		: theme.colors.background
																}
																transition
																onClick={() =>
																	setCourt({
																		courtNumber: Object.keys(
																			getSelectedSchool().courts[
																				sport
																			],
																		)[0],
																		courtsSport: sport,
																	})
																}>
																<Text
																	textWeight={
																		court.courtsSport === sport &&
																		"bold"
																	}
																	textColor={
																		court.courtsSport === sport &&
																		theme.colors.white
																	}>
																	{getSport(sport).sportName}
																</Text>
															</Div>
														),
													)}
												</Row>
											)}
											<InlineCheckboxButtons
												elements={Object.keys(
													getSelectedSchool().courts[
														court.courtsSport ?? sportID
													],
												).reduce((courtsArray, currentCourt) => {
													courtsArray.push({
														text: currentCourt,
														number: currentCourt,
													})
													return courtsArray
												}, [])}
												activeBg={
													theme.colors[
														getSelectedSchool().courts[
															court.courtsSport ?? sportID
														][court.courtNumber]?.surfaceType
													]
												}
												selectedElem={court.courtNumber}
												handleClick={number =>
													setCourt(prevState => ({
														...prevState,
														courtNumber: number,
													}))
												}
												valueToSet="number"
											/>
											{/* luce e riscaldamento si chiedono solo dove cambiano un
											    prezzo: dove non lo cambiano non c'è niente da chiedere */}
											{(courtExtras.lights || courtExtras.heat) && (
												<Div
													d="flex"
													align="center"
													flexWrap="wrap"
													m={{ b: "0.5rem" }}
													style={{ gap: "0.75rem" }}>
													{courtExtras.lights && (
														<Div
															d="flex"
															align="center"
															p="0.5rem 1rem"
															w="fit-content"
															rounded="15px"
															style={{ gap: "0.5rem", cursor: "pointer" }}
															bg={
																usingLights
																	? theme.colors.yellow_light
																	: theme.colors.gray_light
															}
															transition
															onClick={() => setUsingLights(!usingLights)}>
															<Text
																textSize="subheader"
																textWeight={usingLights && "bold"}>
																Luce
															</Text>
															<FontAwesomeIcon
																icon={
																	usingLights
																		? faLightbulb
																		: faLightbulbOutline
																}
																size="xl"
																color={
																	usingLights
																		? theme.colors.yellow
																		: theme.colors.gunmetal
																}
															/>
														</Div>
													)}
													{courtExtras.heat && (
														<Div
															d="flex"
															align="center"
															p="0.5rem 1rem"
															w="fit-content"
															rounded="15px"
															style={{ gap: "0.5rem", cursor: "pointer" }}
															bg={
																usingHeat
																	? theme.colors.red_light
																	: theme.colors.gray_light
															}
															transition
															onClick={() => setUsingHeat(!usingHeat)}>
															<Text
																textSize="subheader"
																textWeight={usingHeat && "bold"}>
																Riscaldamento
															</Text>
															<FontAwesomeIcon
																icon={faFireAlt}
																size="xl"
																color={
																	usingHeat
																		? theme.colors.red
																		: theme.colors.gunmetal
																}
															/>
														</Div>
													)}
												</Div>
											)}
											{isMatchForm && (
												<Text
													textSize="caption"
													textColor={theme.colors.text_light}
													m={{ b: "0.5rem" }}>
													{matchCourtHint}
												</Text>
											)}
										</Paragraph>
									</>
								)}

								{/* Note e ricorrenza per i tipi speciali, che non passano
								    dallo step di riepilogo */}
								{!hasPlayers && (
									<>
										<TextArea
											placeholder={
												isMatchForm ? "Con chi giochi?" : "Note particolari?"
											}
											value={lessonNotes}
											handleChange={handleLessonNotesChange}
										/>
										{recurrenceFields}
									</>
								)}
							</motion.div>
						</Container>
					)}

					{/* Step 1 - Partecipanti */}
					{visibleStep === 1 && (
						<Container>
							<motion.div
								initial={{ opacity: 0, y: 5 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0 }}>
								{partecipants.map((elem, i) => (
									<Div key={i}>
										<Div d="flex" justify="space-between" align="center">
											<Div
												w={
													showsPrice(partecipants[i]) ||
													tracksPresence(partecipants[i])
														? "75%"
														: "100%"
												}>
												<Paragraph title={`Giocatore ${i + 1}:`}>
													<TextInput
														list="dropdown_list"
														value={partecipants[i].playerName}
														placeholder={`${getPlaceholderLabel(i)} · si può segnare dopo`}
														handleChange={e =>
															handlePartecipantsChange(e, i)
														}
														canPlay={
															activePlayers.length > 0 &&
															playerCanPlay(
																partecipants[i],
																activePlayers,
															)
														}
													/>
													{renderTypedPlayerHint(partecipants[i])}
												</Paragraph>
											</Div>
											{showsPrice(partecipants[i]) && (
												<Div w="20%">
													<Paragraph title="Paga">
														<TextInput
															type="number"
															value={
																partecipants[i] &&
																partecipants[i].paymentAmount
															}
															handleChange={e =>
																handlePlayersInLessonPaymentAmountChange(
																	i,
																	e.target.value,
																)
															}
															suffix={
																<Div
																	pos="absolute"
																	bg="transparent"
																	top="1rem"
																	right="0.5rem">
																	<FontAwesomeIcon icon={faEuro} />
																</Div>
															}
														/>
													</Paragraph>
												</Div>
											)}
											{tracksPresence(partecipants[i]) && (
												<Div w="20%">
													<Paragraph title="Presenza">
														<Label
															d="flex"
															justify="center"
															align="center"
															h="3.5rem"
															m={{ b: "0.5rem" }}
															onClick={() => handlePresenceToggle(i)}>
															<PresenceSwitch
																shadow="0"
																checked={partecipants[i].isPresent !== false}
																activeColor={typeAccent}
																inactiveColor="gray_light"
																text={
																	partecipants[i].isPresent === false
																		? "A"
																		: "P"
																}
															/>
														</Label>
													</Paragraph>
												</Div>
											)}
										</Div>

										{/* chi ha pagato il circolo un metodo non ce l'ha: come ha
										    pagato lo sa il circolo */}
										{partecipants[i].playerName &&
											!paysSchool(partecipants[i], canPaySchool) && (
											<InlineCheckboxButtons
												elements={paymentMethodsFor(partecipants[i]).map(method => ({
													text: getPaymentMethodName(getPaymentMethod, method),
													type: method,
												}))}
												selectedElem={partecipants[i]?.paymentMethod}
												activeBg={typeAccent}
												handleClick={type =>
													handlePlayersInLessonPaymentMethodChange(i, type)
												}
												valueToSet="type"
											/>
										)}

										{paysVisit(partecipants[i]) && (
											<PaidToChoice
												selected={partecipants[i].paidTo ?? coursePaidTo}
												onChange={paidTo => handleVisitPaidToChange(i, paidTo)}
												accent={typeAccent}
											/>
										)}

										{partecipants[i].playerName &&
											canPaySchool &&
											paidToSchoolSwitch(partecipants[i], () => handlePaidToSchoolToggle(i))}

										{tracksPresence(partecipants[i]) &&
											partecipants[i].isPresent === false &&
											partecipants[i].playerName &&
											(() => {
												const absent = partecipants[i]
												const substitute = substituteOf(substitutes, absent)

												if (!substitute)
													return (
														<Button
															w="100%"
															h="3.5rem"
															bg="transparent"
															rounded="20px"
															textColor={theme.colors.gunmetal}
															textSize="subheader"
															m={{ b: "1rem" }}
															pos="relative"
															style={{ gap: "0.5rem" }}
															onClick={() => updateSubstitute(absent, {})}>
															<DashedOutline
																color={theme.colors.gunmetal}
																width={2}
																radius={20}
															/>
															<FontAwesomeIcon icon={faPlus} />
															<Text tag="span">
																Sostituto di {absent.playerName}
															</Text>
														</Button>
													)

												return (
													<Div
														m={{ l: "1rem", b: "1rem" }}
														p={{ l: "0.75rem" }}
														style={{
															borderLeft: `3px solid ${theme.colors.gunmetal}`,
														}}>
														<Div d="flex" justify="space-between" align="flex-end">
															<Div
																w={
																	substituteShowsPrice(substitute)
																		? "75%"
																		: "85%"
																}>
																<Paragraph
																	title={`Sostituto di ${absent.playerName}:`}>
																	<TextInput
																		list="dropdown_list"
																		value={substitute.playerName}
																		handleChange={e =>
																			handleSubstituteChange(e, absent)
																		}
																	/>
																	{renderTypedPlayerHint(substitute)}
																</Paragraph>
															</Div>
															{substituteShowsPrice(substitute) ? (
																<Div w="20%">
																	<Paragraph title="Paga">
																		<TextInput
																			type="number"
																			value={substitute.paymentAmount}
																			handleChange={e =>
																				handleSubstitutePaymentAmountChange(
																					absent,
																					e.target.value,
																				)
																			}
																		/>
																	</Paragraph>
																</Div>
															) : (
																// alla stessa altezza del campo accanto, che
																// ha il titolo sopra e il margine sotto
																<Button
																	h="3.5rem"
																	w="3.5rem"
																	p="0"
																	m={{ b: "0.5rem" }}
																	d="flex"
																	align="center"
																	justify="center"
																	bg="transparent"
																	aria-label={`Togli il sostituto di ${absent.playerName}`}
																	onClick={() => removeSubstitute(absent)}>
																	<FontAwesomeIcon
																		icon={faXmark}
																		size="lg"
																		color={theme.colors.red}
																	/>
																</Button>
															)}
														</Div>
														{substitute.playerName &&
															!paysSchool(substitute, canSubstitutePaySchool) && (
															<InlineCheckboxButtons
																elements={substitutePaymentMethodsFor(substitute).map(
																	method => ({
																		text: getPaymentMethodName(getPaymentMethod, method),
																		type: method,
																	}),
																)}
																selectedElem={substitute.paymentMethod}
																activeBg={typeAccent}
																handleClick={type =>
																	handleSubstitutePaymentMethodChange(absent, type)
																}
																valueToSet="type"
															/>
														)}
														{isCourse &&
															substitute.playerName &&
															substituteShowsPrice(substitute) && (
																<PaidToChoice
																	selected={substitute.paidTo ?? coursePaidTo}
																	onChange={paidTo =>
																		updateSubstitute(absent, { paidTo })
																	}
																	accent={typeAccent}
																/>
															)}
														{substitute.playerName &&
															canSubstitutePaySchool &&
															paidToSchoolSwitch(substitute, () =>
																handleSubstitutePaidToSchoolToggle(absent, substitute),
															)}
														{substituteShowsPrice(substitute) && (
															<Button
																h="2.75rem"
																bg="transparent"
																textColor={theme.colors.red}
																p="0"
																m={{ b: "0.5rem" }}
																style={{ gap: "0.5rem" }}
																onClick={() => removeSubstitute(absent)}>
																<FontAwesomeIcon icon={faXmark} />
																<Text tag="span" textColor={theme.colors.red}>
																	Togli il sostituto
																</Text>
															</Button>
														)}
													</Div>
												)
											})()}

										{/* Bottone elimina giocatore: solo in Edit */}
										{isEdit && totalPlayers > 1 && (
											<motion.div whileTap={{ scale: 0.95 }}>
												<Button
													w="100%"
													d="flex"
													align="center"
													justify="center"
													style={{ gap: "0.5rem" }}
													bg={theme.colors.red}
													textColor={theme.colors.white}
													rounded={theme.rounded["15px"]}
													m={{ x: "auto", b: "1rem" }}
													onClick={() => {
														var remainingPartecipants = partecipants.slice()
														remainingPartecipants.splice(i, 1)
														setPartecipants(remainingPartecipants)
														setTotalPlayers(totalPlayers - 1)
													}}>
													<FontAwesomeIcon icon={faTrash} size="lg" />
													<Text tag="span"> Elimina giocatore</Text>
												</Button>
											</motion.div>
										)}

										{i < partecipants.length - 1 && (
											<Divider w="2px solid" borderColor={"surface"} />
										)}
									</Div>
								))}
							</motion.div>
						</Container>
					)}

					{/* Step 2 - Riepilogo */}
					{visibleStep === 2 && (
						<Container>
							<motion.div
								initial={{ opacity: 0, y: 5 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0 }}>
								<Paragraph
									title={
										<>
											<LessonTypeLabel
												lessonTypeID={lessonType}
												lessonType={getLessonType(lessonType)}
											/>{" "}
											del {DateTime.fromISO(lessonDate).toFormat("dd/MM")}:{" "}
											{lessonStartTime} - {lessonEndTime}
										</>
									}>
									{/* il livello si ricontrolla qui, con tutto il resto */}
									{savedSatLevel && (
										<Div
											d="flex"
											align="center"
											m={{ b: "0.5rem" }}
											style={{ gap: "0.5rem" }}>
											<Text textSize="subheader">Livello:</Text>
											<Div
												w="0.9rem"
												h="0.9rem"
												rounded="circle"
												bg={theme.colors[savedSatLevel.color]}
											/>
											<Text textSize="subheader" textWeight="bold">
												{savedSatLevel.label}
											</Text>
										</Div>
									)}
									{savedPtrLevel && (
										<Div
											d="flex"
											align="center"
											m={{ b: "0.5rem" }}
											style={{ gap: "0.5rem" }}>
											<Text textSize="subheader">Livello PTR:</Text>
											<Text textSize="subheader" textWeight="bold">
												{savedPtrLevel.label}
											</Text>
										</Div>
									)}
									{Object.keys(partecipants).map((player, i) => (
										<Text key={i} textSize="subheader">
											{isPlaceholderPartecipant(partecipants[player]) ? (
												<Text tag="span" textColor="text_light">
													{getPlaceholderLabel(i)}
												</Text>
											) : (
												partecipants[player].playerName
											)}
											{/* in una lezione del circolo l'allievo non
											    paga niente: dire "0 €" sarebbe un conto,
											    e qui non c'è nessun conto. E un posto
											    senza nome non paga finché non ce l'ha */}
											{isPlaceholderPartecipant(partecipants[player]) ? (
												<Text tag="span" textColor="text_light">
													{" "}
													· da segnare
												</Text>
											) : (!isBilledToSchool ||
												paysVisit(partecipants[player])) && (
												<>
													:{" "}
													{partecipants[player].paymentMethod ===
													"subscription" ? (
														"è abbonato"
													) : partecipants[player].paymentMethod ===
													  "recovery" ? (
														"recupera una lezione"
													) : (
														<>
															{partecipants[player].paymentAmount}€{" "}
														{isPaidToSchool(partecipants[player].paidTo)
															? "al circolo"
															: partecipants[player].paymentMethod && (
																	<>
																		con{" "}
																		{
																			getPaymentMethod(
																				partecipants[player]
																					.paymentMethod,
																			)?.paymentName
																		}
																		{paysVisit(partecipants[player]) &&
																			", da dare al circolo"}
																	</>
																)}
														</>
													)}
												</>
											)}
										</Text>
									))}
									{keptSubstitutes(
										substitutes.filter(substitute => substitute.playerName?.trim()),
										partecipants,
									).map((substitute, i) => (
										<Text key={`sostituto-${i}`} textSize="subheader">
											{substitute.playerName}: sostituisce{" "}
											{partecipants.find(
												player =>
													substituteKey(player) === substitute.substituteFor,
											)?.playerName}
											{substitute.paymentMethod === "recovery"
												? " e recupera una lezione"
												: paysSchool(substitute, canSubstitutePaySchool) ||
													  (isCourse &&
															(substitute.paidTo ?? coursePaidTo) ===
																SUBSTITUTE_PAID_TO_SCHOOL)
													? `, ${substitute.paymentAmount}€ al circolo`
													: substitute.paymentMethod
														? `, ${substitute.paymentAmount}€ con ${getPaymentMethodName(getPaymentMethod, substitute.paymentMethod)}${isCourse ? ", da dare al circolo" : ""}`
														: ""}
										</Text>
									))}
									{/* Il campo in un blocco solo: chiuso è la riga che
									    serve per controllare, aperto quello che serve per
									    capire — superficie, chi paga e quanto. */}
									{court && getSelectedSchool() && (
										<CourtBlock
											courtNumber={court.courtNumber}
											surfaceColor={theme.colors[courtSurface?.surfaceType]}
											sportIcon={
												sportID === "atletica"
													? getSportIcon(court.courtsSport ?? sportID)
													: null
											}
											usingLights={usingLights}
											usingHeat={usingHeat}
											courtPaidBy={isBilledToSchool ? null : lessonCourtPaidBy}
											onCourtPaidByChange={handleCourtPaidByChange}
											money={isBilledToSchool ? null : courtMoney}
											hint={courtPaidByHint()}
											expandable={!isBilledToSchool}
											fitContent={isBilledToSchool}
										/>
									)}
									<TextArea
										placeholder="Note particolari?"
										value={lessonNotes}
										handleChange={handleLessonNotesChange}
									/>

									{recurrenceFields}
								</Paragraph>
							</motion.div>
						</Container>
					)}

					{activePlayers.length > 0 && (
						<datalist id="dropdown_list">
							{/* L'elenco mostra il soprannome, perché è lì che due omonimi
							    si distinguono; il nome che finisce nella lezione resta
							    però quello vero, e viaggia in `data-name`. */}
							{activePlayers.map(player => (
								<option
									key={player.playerID}
									data-id={player.playerID}
									data-name={getPlayerFullName(player)}
									value={getPlayerLabel(player)}
								/>
							))}
						</datalist>
					)}
				</Div>
			</Div>

			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={theme.colors.gunmetal}
							text={
								isSubmitting
									? isEdit
										? isMatchForm
											? "Modificando..."
											: "Modificando la lezione..."
										: isMatchForm
											? "Aggiungendo..."
											: "Aggiungendo la lezione..."
									: "Indietro"
							}
							textSize="subheader"
							action={() => {
								// durante il salvataggio il tasto dice cosa sta
								// succedendo, non è più una via d'uscita
								if (isSubmitting) return
								switch (formStep) {
									case 0:
										closeModal()
										break
									default:
										setFormStep(formStep - 1)
								}
							}}
						/>
					</Col>
					{!isSubmitting && (
						<Col p="0">
							<PrimaryButton
								m="auto"
								bg={typeAccent}
								textColor="white"
								textSize="subheader"
								text={isConfirmStep ? "Conferma" : "Avanti"}
								disabled={lessonCoachLocked || isDisabled()}
								action={() => {
									if (lessonCoachLocked) return
									if (isConfirmStep) {
										handleSubmit()
									} else {
										setFormStep(formStep + 1)
									}
								}}
							/>
						</Col>
					)}{" "}
				</Row>
			</Div>
		</>
	)
}

export default LessonFormModal
